import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  COUNTY_COURT_OUTREACH_REPLIES_OPENED_EVENT,
  countUnreadCountyCourtOutreachReplies,
  getCountyCourtOutreachRepliesLastOpenedAt,
  setCountyCourtOutreachRepliesLastOpenedAt,
  type CountyCourtOutreachReplyForUnread,
} from '@/lib/countyCourtOutreachRepliesStorage';

export function useCountyCourtOutreachUnreadReplies(
  replies: CountyCourtOutreachReplyForUnread[] | undefined,
) {
  const [lastOpenedAt, setLastOpenedAt] = useState<string | null>(() =>
    getCountyCourtOutreachRepliesLastOpenedAt(),
  );

  useEffect(() => {
    const onOpened = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      setLastOpenedAt(detail ?? getCountyCourtOutreachRepliesLastOpenedAt());
    };
    window.addEventListener(COUNTY_COURT_OUTREACH_REPLIES_OPENED_EVENT, onOpened);
    return () => {
      window.removeEventListener(
        COUNTY_COURT_OUTREACH_REPLIES_OPENED_EVENT,
        onOpened,
      );
    };
  }, []);

  const unreadCount = useMemo(
    () => countUnreadCountyCourtOutreachReplies(replies ?? [], lastOpenedAt),
    [replies, lastOpenedAt],
  );

  const markRepliesOpened = useCallback(() => {
    const at = new Date().toISOString();
    setCountyCourtOutreachRepliesLastOpenedAt(at);
    setLastOpenedAt(at);
  }, []);

  return {
    unreadCount,
    hasUnread: unreadCount > 0,
    lastOpenedAt,
    markRepliesOpened,
  };
}
